import axios from "axios";
import React, { useEffect, useMemo, useState } from "react";
import {
  Calendar2Week,
  Clock,
  InfoCircleFill,
  Person,
  X,
} from "react-bootstrap-icons";
import { ApiConnectionString } from "../constants/ApiConntection";
import { useAuthContext } from "../hooks/useAuthContext";
import LoadingSpinner from "../components/LoadingSpinner";
import "./Modal.css";
import "./CreateAppointmentModal.css";

export default function CreateAppointmentModal({
  availability,
  appointments = [],
  show,
  onClose,
  onCreated,
  profile,
  startDate = "",
  startTime = "",
}) {
  const [therapySession, setTherapySession] = useState(true);
  const [clientId, setClientId] = useState("");
  const [name, setName] = useState("");

  const [selectedStartDate, setSelectedStartDate] = useState("");
  const [selectedStartTime, setSelectedStartTime] = useState("");

  const [finishTime, setFinishTime] = useState("");

  const [actualSessionTime, setActualSessionTime] = useState(50);
  const [bufferTime, setBufferTime] = useState(10);
  const [showDurationOptions, setShowDurationOptions] = useState(false);

  const [clients, setClients] = useState([]);
  const [sendEmailToClient, setSendEmailToClient] = useState(false);

  const [validated, setValidated] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [err, setErr] = useState("");

  const { user } = useAuthContext();

  const durationMinutes = Number(actualSessionTime) + Number(bufferTime);

  const dayKeys = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];

  const timeOptions = useMemo(() => {
    const options = [];

    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const h = String(hour).padStart(2, "0");
        const m = String(minute).padStart(2, "0");

        options.push(`${h}:${m}`);
      }
    }

    return options;
  }, []);

  const sessionLengthOptions = useMemo(() => {
    return [15, 30, 45, 50, 60, 75, 90, 105, 120, 135, 150, 165, 180];
  }, []);

  const bufferOptions = useMemo(() => {
    return [0, 5, 10, 15, 20, 25, 30];
  }, []);

  const selectedClient = clients.find(
    (client) => String(client.id || client.Id) === String(clientId)
  );

  const selectedClientEmail =
    selectedClient?.email || selectedClient?.Email || "";

  const selectedClientEmailVerified =
    selectedClient?.emailVerified ?? selectedClient?.EmailVerified ?? false;

  const buildDateTime = (date, time) => {
    if (!date || !time) {
      return "";
    }

    return `${date}T${time}`;
  };

  const formatDateTimeForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const addMinutesToDateTime = (dateTime, minutes) => {
    if (!dateTime) {
      return "";
    }

    const date = new Date(dateTime);
    date.setMinutes(date.getMinutes() + Number(minutes));

    return formatDateTimeForInput(date);
  };

  const titleCase = (value) => {
    if (!value) {
      return "";
    }

    return value.charAt(0).toUpperCase() + value.slice(1);
  };

  const normaliseTime = (value) => {
    if (!value) {
      return "";
    }

    return value.slice(0, 5);
  };

  const timeToMinutes = (value) => {
    if (!value) {
      return 0;
    }

    const [hour, minute] = value.slice(0, 5).split(":").map(Number);

    return hour * 60 + minute;
  };

  const finishTimeOptions = useMemo(() => {
    if (!selectedStartTime) {
      return [];
    }

    const startMinutes = timeToMinutes(selectedStartTime);

    return timeOptions.filter(
      (time) => timeToMinutes(time) > startMinutes
    );
  }, [timeOptions, selectedStartTime]);

  const getDayKeyFromDate = (dateValue) => {
    if (!dateValue) {
      return "";
    }

    const [year, month, day] = dateValue.split("-").map(Number);
    const date = new Date(year, month - 1, day);

    return dayKeys[date.getDay()];
  };

  const getAvailabilityForDate = (dateValue) => {
    if (!availability || !dateValue) {
      return null;
    }

    const dayKey = getDayKeyFromDate(dateValue);

    return {
      dayKey,
      dayLabel: titleCase(dayKey),
      available: Boolean(availability[`${dayKey}Available`]),
      start: normaliseTime(availability[`${dayKey}StartTime`]),
      end: normaliseTime(availability[`${dayKey}EndTime`]),
    };
  };

  const getAppointmentStart = (appointment) => {
    return (
      appointment.start ||
      appointment.Start ||
      appointment.appointmentDate ||
      appointment.AppointmentDate ||
      appointment.dateAndTime ||
      appointment.DateAndTime ||
      ""
    );
  };

  const getAppointmentFinish = (appointment) => {
    return (
      appointment.finish ||
      appointment.Finish ||
      appointment.finishTime ||
      appointment.FinishTime ||
      ""
    );
  };

  const dateRangesOverlap = (startA, finishA, startB, finishB) => {
    if (!startA || !finishA || !startB || !finishB) {
      return false;
    }

    const aStart = new Date(startA);
    const aFinish = new Date(finishA);
    const bStart = new Date(startB);
    const bFinish = new Date(finishB);

    return aStart < bFinish && aFinish > bStart;
  };

  const overlapsExistingAppointment = (start, finish) => {
    return appointments.some((appointment) => {
      const appointmentStart = getAppointmentStart(appointment);
      const appointmentFinish = getAppointmentFinish(appointment);

      return dateRangesOverlap(
        start,
        finish,
        appointmentStart,
        appointmentFinish
      );
    });
  };

  const getTherapyFinish = () => {
    const start = buildDateTime(selectedStartDate, selectedStartTime);

    if (!start) {
      return "";
    }

    return addMinutesToDateTime(start, durationMinutes);
  };

  const isStartTimeAvailable = (dateValue, time) => {
    const dayAvailability = getAvailabilityForDate(dateValue);

    if (
      !dayAvailability ||
      !dayAvailability.available ||
      !dayAvailability.start ||
      !dayAvailability.end
    ) {
      return false;
    }

    const selectedTime = timeToMinutes(time);
    const availableStart = timeToMinutes(dayAvailability.start);
    const availableEnd = timeToMinutes(dayAvailability.end);

    if (therapySession) {
      const selectedFinish = selectedTime + Number(durationMinutes);

      return selectedTime >= availableStart && selectedFinish <= availableEnd;
    }

    return selectedTime >= availableStart && selectedTime < availableEnd;
  };

  const isStartTimeBooked = (dateValue, time) => {
    if (!dateValue || !time) {
      return false;
    }

    const start = buildDateTime(dateValue, time);

    const finish = therapySession
      ? addMinutesToDateTime(start, durationMinutes)
      : addMinutesToDateTime(start, 15);

    return overlapsExistingAppointment(start, finish);
  };

  const selectedDayAvailability = getAvailabilityForDate(selectedStartDate);

  const startTimeGroups = useMemo(() => {
    const availableTimes = [];
    const unavailableTimes = [];
    const bookedTimes = [];

    timeOptions.forEach((time) => {
      const isBooked =
        selectedStartDate && isStartTimeBooked(selectedStartDate, time);

      if (isBooked) {
        bookedTimes.push(time);
        return;
      }

      if (selectedStartDate && isStartTimeAvailable(selectedStartDate, time)) {
        availableTimes.push(time);
      } else {
        unavailableTimes.push(time);
      }
    });

    return {
      availableTimes,
      unavailableTimes,
      bookedTimes,
    };
  }, [
    timeOptions,
    selectedStartDate,
    availability,
    appointments,
    therapySession,
    durationMinutes,
  ]);

  useEffect(() => {
    if (!show) {
      return;
    }

    const defaultDiaryTime = Number(
      profile?.sessionDefaultTime || profile?.SessionDefaultTime || 60
    );

    const savedActualTime = Number(
      profile?.actualSessionTime || profile?.ActualSessionTime || 0
    );

    const defaultActualTime =
      savedActualTime > 0
        ? savedActualTime
        : defaultDiaryTime === 60
          ? 50
          : defaultDiaryTime;

    const defaultBufferTime = Math.max(
      defaultDiaryTime - defaultActualTime,
      0
    );

    setSelectedStartDate(startDate || "");
    setSelectedStartTime(startTime || "");

    setFinishTime("");

    setClientId("");
    setName("");
    setSendEmailToClient(false);
    setShowDurationOptions(false);
    setValidated(false);
    setErr("");

    setActualSessionTime(defaultActualTime);
    setBufferTime(defaultBufferTime);
  }, [
    show,
    startDate,
    startTime,
    profile?.sessionDefaultTime,
    profile?.SessionDefaultTime,
    profile?.actualSessionTime,
    profile?.ActualSessionTime,
  ]);

  useEffect(() => {
    if (!show || !user?.token) {
      return;
    }

    const getClients = async () => {
      try {
        const response = await axios.get(`${ApiConnectionString}/api/Clients`, {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        });

        if (response.status === 200) {
          const activeClients = (response.data || []).filter((client) => {
            const isArchived = client.isArchived ?? client.IsArchived ?? false;
            const isActive = client.isActive ?? client.IsActive ?? true;

            return isArchived === false && isActive === true;
          });

          setClients(activeClients);
        }
      } catch (error) {
        console.error(error);
        setErr("Failed to load clients.");
      }
    };

    getClients();
  }, [show, user?.token]);

  useEffect(() => {
    if (!selectedStartDate || !selectedStartTime) {
      return;
    }

    if (isStartTimeBooked(selectedStartDate, selectedStartTime)) {
      setSelectedStartTime("");
    }
  }, [
    selectedStartDate,
    selectedStartTime,
    appointments,
    durationMinutes,
    therapySession,
  ]);

  useEffect(() => {
    if (!selectedClientEmail) {
      setSendEmailToClient(false);
    }
  }, [selectedClientEmail]);

  useEffect(() => {
    if (
      therapySession ||
      !selectedStartTime ||
      !finishTime
    ) {
      return;
    }

    if (
      timeToMinutes(finishTime) <=
      timeToMinutes(selectedStartTime)
    ) {
      setFinishTime("");
    }
  }, [therapySession, selectedStartTime, finishTime]);

  const getTherapyFinishDisplay = () => {
    const finish = getTherapyFinish();

    if (!finish) {
      return "";
    }

    const date = new Date(finish);

    return date.toLocaleString("en-GB", {
      weekday: "short",
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const handleStartDateChange = (value) => {
    setSelectedStartDate(value);
    setSelectedStartTime("");
    setFinishTime("");
  };

  const submit = async (e) => {
    e.preventDefault();

    setValidated(true);
    setErr("");

    const form = e.currentTarget;

    if (!form.checkValidity()) {
      return;
    }

    if (therapySession && !clientId) {
      setErr("Please select a client.");
      return;
    }

    const start = buildDateTime(selectedStartDate, selectedStartTime);

    const finish = therapySession
      ? getTherapyFinish()
      : buildDateTime(selectedStartDate, finishTime);

    if (!start || !finish) {
      setErr("Please select a valid date and time.");
      return;
    }

    if (therapySession && durationMinutes <= 0) {
      setErr("Please choose a valid session length.");
      return;
    }

    if (!therapySession && new Date(finish) <= new Date(start)) {
      setErr("Finish time must be after the start time.");
      return;
    }

    if (overlapsExistingAppointment(start, finish)) {
      setErr("This time overlaps with an existing appointment.");
      return;
    }

    setIsLoading(true);

    try {
      const appointment = {
        Id: 0,
        TherapySession: therapySession,
        ClientName: therapySession
          ? `${
              selectedClient?.firstName || selectedClient?.FirstName || ""
            } ${
              selectedClient?.lastName || selectedClient?.LastName || ""
            }`.trim()
          : null,
        ClientId: therapySession ? Number(clientId) : null,
        Name: therapySession ? "Therapy Session" : name,
        Start: start,
        Finish: finish,
        SessionLengthMinutes: therapySession ? Number(actualSessionTime) : null,
        SendEmail:
          therapySession && Boolean(selectedClientEmail) && sendEmailToClient,
      };

      const response = await axios.post(
        `${ApiConnectionString}/api/Appointment`,
        appointment,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );

      if (response.status === 200) {
        if (onCreated) {
          onCreated(response.data);
        }

        onClose();
      }
    } catch (error) {
      console.error(error);
      setErr("Failed to create appointment.");
    } finally {
      setIsLoading(false);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="modal-backdrop-custom appointment-modal-backdrop">
      <div
        className="modal-card-custom appointment-modal-card"
        role="dialog"
        aria-modal="true"
        aria-labelledby="create-appointment-title"
      >
        <div className="modal-header-custom appointment-modal-header">
          <div>
            <h2 id="create-appointment-title">Create appointment</h2>
            <p>Schedule a therapy session or block out time in your diary.</p>
          </div>

          <button
            type="button"
            className="modal-close-btn appointment-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            <X />
          </button>
        </div>

        {isLoading ? (
          <div className="modal-loading">
            <LoadingSpinner />
          </div>
        ) : (
          <form
            className={validated ? "was-validated" : ""}
            onSubmit={submit}
            noValidate
          >
            {err && (
              <div className="alert alert-danger appointment-modal-error">
                {err}
              </div>
            )}

            <div className="modal-toggle appointment-type-toggle">
              <button
                type="button"
                className={therapySession ? "active" : ""}
                onClick={() => setTherapySession(true)}
              >
                <Person />
                <span>Therapy session</span>
              </button>

              <button
                type="button"
                className={!therapySession ? "active" : ""}
                onClick={() => setTherapySession(false)}
              >
                <Calendar2Week />
                <span>Unavailable time</span>
              </button>
            </div>

            {therapySession && (
              <div className="appointment-field">
                <label className="form-label" htmlFor="appointment-client">
                  Client
                </label>

                <div className="appointment-input-wrap">
                  <Person className="appointment-input-icon" />

                  <select
                    id="appointment-client"
                    className="form-control modal-input appointment-input-with-icon"
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    required
                  >
                    <option value="">Select client</option>

                    {clients.map((client) => (
                      <option
                        key={client.id || client.Id}
                        value={client.id || client.Id}
                      >
                        {client.firstName || client.FirstName}{" "}
                        {client.lastName || client.LastName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {therapySession && selectedClient && selectedClientEmail && (
              <div className="appointment-email-option">
                <label className="appointment-email-card">
                  <input
                    type="checkbox"
                    checked={sendEmailToClient}
                    onChange={(e) => setSendEmailToClient(e.target.checked)}
                  />

                  <div>
                    <strong>Send confirmation email</strong>

                    <span>
                      Send the appointment date and time to {selectedClientEmail}.
                    </span>
                  </div>
                </label>

                {!selectedClientEmailVerified && (
                  <div className="appointment-email-warning">
                    This email has not been verified. Check that it is correct
                    before sending.
                  </div>
                )}
              </div>
            )}

            {!therapySession && (
              <div className="appointment-field">
                <label className="form-label" htmlFor="appointment-reason">
                  Reason
                </label>

                <input
                  id="appointment-reason"
                  className="form-control modal-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Lunch, holiday, admin work..."
                  required
                />
              </div>
            )}

            <div className="row appointment-date-time-row">
              <div className="col-md-6 appointment-field">
                <label className="form-label" htmlFor="appointment-start-date">
                  Start date
                </label>

                <div className="appointment-input-wrap">
                  <Calendar2Week className="appointment-input-icon" />

                  <input
                    id="appointment-start-date"
                    type="date"
                    className="form-control modal-input appointment-input-with-icon"
                    value={selectedStartDate}
                    onChange={(e) => handleStartDateChange(e.target.value)}
                    required
                  />
                </div>
              </div>

              <div className="col-md-6 appointment-field">
                <label className="form-label" htmlFor="appointment-start-time">
                  Start time
                </label>

                <div className="appointment-input-wrap">
                  <Clock className="appointment-input-icon" />

                  <select
                    id="appointment-start-time"
                    className="form-control modal-input appointment-input-with-icon"
                    value={selectedStartTime}
                    onChange={(e) => setSelectedStartTime(e.target.value)}
                    required
                  >
                    <option value="">Select time</option>

                    {selectedStartDate ? (
                      <>
                        {startTimeGroups.availableTimes.length > 0 && (
                          <optgroup label="Available times">
                            {startTimeGroups.availableTimes.map((time) => (
                              <option
                                key={`available-${time}`}
                                value={time}
                                className="time-option-available"
                              >
                                {time}
                              </option>
                            ))}
                          </optgroup>
                        )}

                        <optgroup label="Outside availability">
                          {startTimeGroups.unavailableTimes.map((time) => (
                            <option
                              key={`unavailable-${time}`}
                              value={time}
                              className="time-option-muted"
                            >
                              {time}
                            </option>
                          ))}
                        </optgroup>
                      </>
                    ) : (
                      timeOptions.map((time) => (
                        <option key={time} value={time}>
                          {time}
                        </option>
                      ))
                    )}
                  </select>
                </div>
              </div>
            </div>

            {selectedStartDate && selectedDayAvailability && (
              <div className="availability-hint">
                <InfoCircleFill />

                <div>
                  {selectedDayAvailability.available &&
                  selectedDayAvailability.start &&
                  selectedDayAvailability.end ? (
                    <span>
                      <strong>{selectedDayAvailability.dayLabel}</strong>{" "}
                      availability: {selectedDayAvailability.start}–
                      {selectedDayAvailability.end}
                    </span>
                  ) : (
                    <span>
                      <strong>{selectedDayAvailability.dayLabel}</strong> has no
                      availability set. Times are still selectable.
                    </span>
                  )}

                  {startTimeGroups.bookedTimes.length > 0 && (
                    <span className="booked-times-note">
                      Existing appointment times are hidden.
                    </span>
                  )}
                </div>
              </div>
            )}

            {therapySession ? (
              <>
                <div className="appointment-duration-summary">
                  <span className="appointment-summary-icon">
                    <Clock />
                  </span>

                  <div className="appointment-duration-copy">
                    <strong>
                      {Number(actualSessionTime)} min session +{" "}
                      {Number(bufferTime)} min buffer
                    </strong>

                    <span>{durationMinutes} min reserved in your diary.</span>
                  </div>

                  <button
                    type="button"
                    className="appointment-duration-change-btn"
                    onClick={() => setShowDurationOptions((prev) => !prev)}
                  >
                    {showDurationOptions ? "Hide timing" : "Change timing"}
                  </button>
                </div>

                {showDurationOptions && (
                  <div className="appointment-duration-options">
                    <div className="row">
                      <div className="col-md-6 appointment-field">
                        <label
                          className="form-label"
                          htmlFor="appointment-session-length"
                        >
                          Session length
                        </label>

                        <select
                          id="appointment-session-length"
                          className="form-control modal-input"
                          value={actualSessionTime}
                          onChange={(e) =>
                            setActualSessionTime(e.target.value)
                          }
                          required
                        >
                          {sessionLengthOptions.map((minutes) => (
                            <option key={minutes} value={minutes}>
                              {minutes} minutes
                            </option>
                          ))}
                        </select>

                        <small className="appointment-field-help">
                          How long the client session lasts.
                        </small>
                      </div>

                      <div className="col-md-6 appointment-field">
                        <label
                          className="form-label"
                          htmlFor="appointment-buffer-time"
                        >
                          Buffer time
                        </label>

                        <select
                          id="appointment-buffer-time"
                          className="form-control modal-input"
                          value={bufferTime}
                          onChange={(e) => setBufferTime(e.target.value)}
                          required
                        >
                          {bufferOptions.map((minutes) => (
                            <option key={minutes} value={minutes}>
                              {minutes === 0
                                ? "No buffer"
                                : `${minutes} minutes`}
                            </option>
                          ))}
                        </select>

                        <small className="appointment-field-help">
                          Time kept free after the session.
                        </small>
                      </div>
                    </div>
                  </div>
                )}

                {selectedStartDate && selectedStartTime && (
                  <div className="appointment-finish-summary">
                    <span className="appointment-finish-icon">
                      <Calendar2Week />
                    </span>

                    <div>
                      <span>Diary slot will finish at</span>
                      <strong>{getTherapyFinishDisplay()}</strong>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="appointment-field appointment-unavailable-finish">
                <label
                  className="form-label"
                  htmlFor="appointment-finish-time"
                >
                  Finish time
                </label>

                <div className="appointment-input-wrap">
                  <Clock className="appointment-input-icon" />

                  <select
                    id="appointment-finish-time"
                    className="form-control modal-input appointment-input-with-icon"
                    value={finishTime}
                    onChange={(e) => setFinishTime(e.target.value)}
                    disabled={
                      !selectedStartTime ||
                      finishTimeOptions.length === 0
                    }
                    required
                  >
                    <option value="">
                      {!selectedStartTime
                        ? "Choose a start time first"
                        : finishTimeOptions.length === 0
                          ? "No later times available today"
                          : "Select finish time"}
                    </option>

                    {finishTimeOptions.map((time) => (
                      <option key={time} value={time}>
                        {time}
                      </option>
                    ))}
                  </select>
                </div>

                <small className="appointment-field-help">
                  {selectedStartTime
                    ? `Only times after ${selectedStartTime} are shown. The block will finish on the same day.`
                    : "Select the start time before choosing when the block finishes."}
                </small>
              </div>
            )}

            <div className="modal-actions appointment-modal-actions">
              <button
                type="button"
                className="btn modal-cancel-btn"
                onClick={onClose}
              >
                Cancel
              </button>

              <button type="submit" className="btn modal-save-btn">
                Create appointment
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}